import React, { useState, useContext } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMenu, FiX, FiMoon, FiSun } from 'react-icons/fi';
import { ThemeContext } from '../../context/ThemeContext';
import logoLions from '../../assets/images/Logolions.jpg';

interface HeaderProps {
  darkMode?: boolean;
  toggleDarkMode?: () => void;
}

const Header: React.FC<HeaderProps> = ({ darkMode, toggleDarkMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { theme, toggleTheme } = useContext(ThemeContext);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Huidige Site', path: '/nieuw' },
    { name: 'Over ons', path: '/over-ons' },
    { name: 'Projecten', path: '/projecten' },
    { name: 'Nieuws', path: '/nieuws' },
    { name: 'Evenementen', path: '/evenementen' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/95 dark:bg-indigo-900/95 backdrop-blur-sm shadow-lg border-b border-indigo-100 dark:border-indigo-800/30">
      <div className="container mx-auto px-4 md:px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3" onClick={closeMenu}>
            <div className="relative">
              <img 
                src={logoLions} 
                alt="Lions Club Hoorn De Doelen" 
                className="h-12 w-auto rounded-lg shadow-md hover:scale-105 transition-transform duration-300" 
              />
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 to-purple-600/20 rounded-lg"></div>
            </div>
            <div className="hidden md:block">
              <h1 className="text-xl font-bold text-indigo-900 dark:text-white">Lions Club</h1>
              <p className="text-sm text-gray-600 dark:text-gray-300">Hoorn De Doelen</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `text-base font-medium transition-colors duration-300 hover:text-teal-400 ${
                    isActive
                      ? 'text-teal-400 border-b-2 border-teal-400'
                      : 'text-gray-700 dark:text-white'
                  }`
                }
              >
                {item.name}
              </NavLink>
            ))}
            <NavLink
              to="/leden-login"
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
            >
              Leden Login
            </NavLink>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-indigo-800/50 dark:bg-indigo-700/50 text-yellow-300 dark:text-teal-300 hover:bg-indigo-700/70 transition-all duration-300"
              aria-label="Toggle dark mode"
            >
              {theme === 'dark' ? <FiSun size={20} /> : <FiMoon size={20} />}
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <div className="flex items-center space-x-4 md:hidden">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-indigo-800/50 dark:bg-indigo-700/50 text-yellow-300 dark:text-teal-300 hover:bg-indigo-700/70 transition-all duration-300"
              aria-label="Toggle dark mode"
            >
              {theme === 'dark' ? <FiSun size={20} /> : <FiMoon size={20} />}
            </button>
            <button
              onClick={toggleMenu}
              className="p-2 text-gray-700 dark:text-white hover:text-teal-400 transition-colors duration-300"
              aria-label="Toggle menu"
            >
              {isOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="md:hidden bg-gradient-to-b from-indigo-900 to-purple-900 border-t border-teal-500/30"
        >
          <div className="container mx-auto px-4 md:px-8 py-4">
            <nav className="flex flex-col space-y-4">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `text-base font-medium py-2 transition-colors duration-300 ${
                      isActive
                        ? 'text-teal-400 border-l-4 border-teal-400 pl-4'
                        : 'text-white hover:text-teal-400'
                    }`
                  }
                  onClick={closeMenu}
                >
                  {item.name}
                </NavLink>
              ))}
              <NavLink
                to="/leden-login"
                className="bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium py-3 px-4 rounded-lg text-center transition-all duration-300 hover:shadow-lg"
                onClick={closeMenu}
              >
                Leden Login
              </NavLink>
            </nav>
          </div>
        </motion.div>
      )}
    </header>
  );
};

export default Header; 